import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactElement } from 'react';

import { cn } from '../lib/cn.js';

// shadcn-style Button. The semantic-token references mean a palette change in
// `@nuberu/ui` re-skins every button automatically.
//
// Touch targets — every variant has a minimum 44px tap area on mobile
// (h-11 sm:h-9 — full on mobile, compact on desktop) per CLAUDE.md's
// responsive requirement.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-(--radius-lg) font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-contrast hover:bg-primary-muted active:bg-primary-muted',
        secondary: 'bg-surface-3 text-text-primary hover:bg-surface-4 active:bg-surface-4',
        ghost: 'bg-transparent text-text-primary hover:bg-surface-3 active:bg-surface-3',
        outline: 'bg-transparent text-primary-muted border border-border-strong hover:bg-surface-3',
        // Bordered-grey secondary — for "Cancelar"/dismiss actions that shouldn't
        // read as teal (the outline variant's ink). Distinct from `secondary`
        // (filled) and `ghost` (borderless).
        neutral:
          'bg-transparent text-text-secondary border border-border-strong hover:bg-surface-3 hover:text-text-primary',
        danger:
          'bg-status-warning text-white hover:bg-status-warning/90 active:bg-status-warning/80',
      },
      size: {
        sm: 'h-11 sm:h-8 px-3 text-sm',
        md: 'h-11 sm:h-9 px-4 text-sm',
        lg: 'h-12 sm:h-10 px-6 text-base',
        icon: 'h-11 w-11 sm:h-9 sm:w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps): ReactElement => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
};

export { buttonVariants };
